/* ============================================================
   customerOrderFilter — TASK-16aeb9c6 (audit-2 P5): pure derive
   model for the customer OrdersPage status filter. Copies the
   provider filter-chip pattern (ProviderOrdersPanel, TASK-256):
   `incoming` bucket = Pending, `active` bucket = work in flight
   **including Disputed** (a dispute is not over — the customer
   still owes it attention), `done` terminal bucket = Completed +
   Cancelled. Zero React dependency, plain data in / plain data
   out, so the module runs under `node --test`
   (tests/customerOrderFilter.test.ts) as well as in the app
   bundle. No API change (flowwow-parity-audit-2.md row 6).
   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { WebOrderStatus } from '../types/product';

export type CustomerOrderFilter = 'all' | 'incoming' | 'active' | 'done';

/** Bucket a single order belongs to (the `all` chip is not a bucket). */
export type CustomerOrderBucket = 'incoming' | 'active' | 'done';

/** Chip order matches the provider queue: Все → Новые → В работе → Готово. */
export const CUSTOMER_ORDER_FILTERS: readonly CustomerOrderFilter[] = ['all', 'incoming', 'active', 'done'];

const ACTIVE_STATUSES: readonly WebOrderStatus[] = ['Accepted', 'Printing', 'Printed', 'Shipped', 'Disputed'];

/* Wire statuses arrive as the string union or the API's numeric enum.
 * Mirrors types/product ORDER_STATUS_BY_INDEX (0 Pending … 7 Printed) so
 * this module keeps zero runtime imports — like shippedTrackingView, it
 * then loads under `node --test` exactly as it ships in the app bundle.
 * tests/customerOrderFilter.test.ts pins the two maps together. */
const STATUS_BY_INDEX: readonly WebOrderStatus[] = [
  'Pending', 'Accepted', 'Printing', 'Shipped', 'Completed', 'Cancelled', 'Disputed', 'Printed',
];

function normalizeStatus(status: WebOrderStatus | number): WebOrderStatus {
  return typeof status === 'number' ? (STATUS_BY_INDEX[status] ?? 'Pending') : status;
}

/** Bucket membership — accepts the raw wire status (string union or numeric
 *  enum, normalized exactly like every other Web view model). */
export function customerOrderBucket(status: WebOrderStatus | number): CustomerOrderBucket {
  const s = normalizeStatus(status);
  if (s === 'Pending') return 'incoming';
  if (ACTIVE_STATUSES.includes(s)) return 'active';
  return 'done'; // terminal bucket: Completed / Cancelled
}

export interface CustomerOrderFilterChip {
  filter: CustomerOrderFilter;
  /** RU chip label with the live count, e.g. «В работе (3)». */
  label: string;
  count: number;
}

/** Chip models with counts for a whole list — one derive call per render. */
export function buildCustomerOrderFilterChips(
  orders: ReadonlyArray<{ status: WebOrderStatus | number }>,
): CustomerOrderFilterChip[] {
  const counts: Record<CustomerOrderFilter, number> = { all: orders.length, incoming: 0, active: 0, done: 0 };
  for (const order of orders) {
    counts[customerOrderBucket(order.status)] += 1;
  }
  return customerOrderChipsFromCounts(counts);
}

/** 4171ba09: chips from server-side bucket counts (paginated cabinet) —
 * counts cover the customer's whole history, not just the loaded page;
 * labels are shared with the in-memory variant so both surfaces agree. */
export function customerOrderChipsFromCounts(
  bucketCounts: Record<string, number>,
): CustomerOrderFilterChip[] {
  const counts: Record<CustomerOrderFilter, number> = {
    all: bucketCounts.all ?? 0,
    incoming: bucketCounts.incoming ?? 0,
    active: bucketCounts.active ?? 0,
    done: bucketCounts.done ?? 0,
  };
  return CUSTOMER_ORDER_FILTERS.map((filter) => ({
    filter,
    count: counts[filter],
    label:
      filter === 'all' ? `Все (${counts.all})` :
      filter === 'incoming' ? `Ожидают (${counts.incoming})` :
      filter === 'active' ? `В работе (${counts.active})` :
      `Завершены (${counts.done})`,
  }));
}

/** Filter a list while preserving order and row identity (same objects out). */
export function filterCustomerOrders<T extends { status: WebOrderStatus | number }>(
  orders: readonly T[],
  filter: CustomerOrderFilter,
): T[] {
  if (filter === 'all') return [...orders];
  return orders.filter((o) => customerOrderBucket(o.status) === filter);
}

/** Filter-aware empty state — says what lands in this tab (TASK-255 style),
 *  instead of a generic “nothing here”. */
export function customerOrderFilterEmptyText(filter: CustomerOrderFilter): string {
  switch (filter) {
    case 'incoming':
      return 'Заказов, ожидающих исполнителя, нет. Новый заказ появится здесь сразу после оформления.';
    case 'active':
      return 'Активных заказов нет. Принятые в работу, печатаемые, отправленные и спорные заказы будут видны здесь.';
    case 'done':
      return 'Завершённых и отменённых заказов пока нет.';
    default:
      return 'Заказов пока нет.';
  }
}
